import { useState, useEffect, useRef } from 'react';
import useLatestCallback from '@/hooks/useLatestCallback';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ChevronLeft, MapPin, Phone, Clock, CheckCircle, Package, Truck, Store,
  Receipt, ChefHat, XCircle, Download, Mail, Loader2, RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuthStore } from '@/store';

import { API } from '@/config/api';

const statusIcons = {
  receipt: Receipt,
  'check-circle': CheckCircle,
  'chef-hat': ChefHat,
  package: Package,
  truck: Truck,
  'check-circle-2': CheckCircle,
  'x-circle': XCircle,
  circle: Clock
};

export default function OrderTrackingPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuthStore();
  const [tracking, setTracking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [downloadingReceipt, setDownloadingReceipt] = useState(false);
  const [emailingReceipt, setEmailingReceipt] = useState(false);
  const wsRef = useRef(null);

  const fetchTracking = useLatestCallback(async () => {
    try {
      const response = await axios.get(`${API}/tracking/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTracking(response.data);
    } catch (error) {
      console.error('Error fetching tracking:', error);
      if (error.response?.status === 404) {
        toast.error('Order not found');
        navigate('/orders');
      }
    } finally {
      setLoading(false);
    }
  });

  const setupWebSocket = useLatestCallback(() => {
    const wsUrl = `${process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://')}/api/tracking/ws/${id}`;
    
    try {
      wsRef.current = new WebSocket(wsUrl);
      
      wsRef.current.onopen = () => setConnected(true);
      wsRef.current.onclose = () => setConnected(false);
      wsRef.current.onerror = () => setConnected(false);
      
      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'status_update') {
            // Refresh full tracking data on status change
            fetchTracking();
          }
        } catch (e) {
          console.error('WebSocket parse error:', e);
        }
      };
    } catch (e) {
      console.error('WebSocket connection error:', e);
    }
  });

  useEffect(() => {
    fetchTracking();
    setupWebSocket();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchTracking, 30000);
    
    return () => {
      clearInterval(interval);
      wsRef.current?.close();
    };
  }, [id, fetchTracking, setupWebSocket]);

  const handleDownloadReceipt = async () => {
    setDownloadingReceipt(true);
    try {
      const response = await axios.get(`${API}/receipts/${id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `receipt_order_${id}.html`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Receipt downloaded!');
    } catch (error) {
      toast.error('Failed to download receipt');
    } finally {
      setDownloadingReceipt(false);
    }
  };

  const handleEmailReceipt = async () => {
    setEmailingReceipt(true);
    try {
      await axios.get(`${API}/receipts/email/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Receipt sent to your email!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to email receipt');
    } finally {
      setEmailingReceipt(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background pb-20">
        <header className="sticky top-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b">
          <div className="container flex h-14 items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <Skeleton className="h-6 w-40" />
          </div>
        </header>
        <main className="container py-6 space-y-6">
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-32 w-full" />
        </main>
      </div>
    );
  }

  if (!tracking) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground">Order not found</p>
          <Button onClick={() => navigate('/orders')} className="mt-4">
            View All Orders
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b">
        <div className="container flex h-14 items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="font-semibold">Order #{tracking.order_id}</h1>
              <p className="text-xs text-muted-foreground">Track your order</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant={connected ? "default" : "secondary"} className="text-xs">
              {connected ? "Live" : "Offline"}
            </Badge>
            <Button variant="ghost" size="icon" onClick={fetchTracking}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="container py-6 space-y-6">
        {/* Status Card */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">{tracking.status_display}</CardTitle>
              {tracking.estimated_delivery && (
                <Badge variant="outline" className="text-sm">
                  <Clock className="h-3 w-3 mr-1" />
                  ETA: {tracking.estimated_delivery}
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {/* Status Timeline */}
            <div className="relative mt-4">
              {tracking.status_timeline?.map((step, index) => {
                const IconComponent = statusIcons[step.icon] || Clock;
                
                return (
                  <div key={step.status} className="flex items-start gap-4 mb-6 last:mb-0">
                    <div className={`
                      w-10 h-10 rounded-full flex items-center justify-center shrink-0 transition-all duration-500
                      ${step.completed 
                        ? 'bg-primary text-primary-foreground' 
                        : 'bg-muted text-muted-foreground'
                      }
                      ${step.current ? 'ring-4 ring-primary/20 scale-110' : ''}
                    `}>
                      <IconComponent className="h-5 w-5" />
                    </div>
                    <div className="flex-1 pt-2">
                      <p className={`font-medium transition-colors duration-300 ${step.completed ? '' : 'text-muted-foreground'}`}>
                        {step.display}
                      </p>
                      {step.current && tracking.status !== 'delivered' && tracking.status !== 'completed' && (
                        <p className="text-sm text-primary animate-pulse">In progress...</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Driver Info (if available) */}
        {tracking.driver_info && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Truck className="h-5 w-5" />
                Your Driver
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                {tracking.driver_info.photo ? (
                  <img 
                    src={tracking.driver_info.photo} 
                    alt={tracking.driver_info.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                    <Truck className="h-6 w-6 text-muted-foreground" />
                  </div>
                )}
                <div className="flex-1">
                  <p className="font-medium">{tracking.driver_info.name}</p>
                  {tracking.driver_info.vehicle && (
                    <p className="text-sm text-muted-foreground">{tracking.driver_info.vehicle}</p>
                  )}
                </div>
                {tracking.driver_info.phone && (
                  <Button variant="outline" size="icon" asChild>
                    <a href={`tel:${tracking.driver_info.phone}`}>
                      <Phone className="h-4 w-4" />
                    </a>
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Order Details */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Order Details</CardTitle>
              <Badge variant="outline">{tracking.order_type}</Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Restaurant Info */}
            <div className="flex items-start gap-3">
              <Store className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div>
                <p className="font-medium">{tracking.restaurant_info.name}</p>
                <p className="text-sm text-muted-foreground">{tracking.restaurant_info.address}</p>
                {tracking.restaurant_info.phone && (
                  <a href={`tel:${tracking.restaurant_info.phone}`} className="text-sm text-primary">
                    {tracking.restaurant_info.phone}
                  </a>
                )}
              </div>
            </div>

            {/* Delivery Address */}
            {tracking.delivery_address && (
              <>
                <Separator />
                <div className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="font-medium">Delivery Address</p>
                    <p className="text-sm text-muted-foreground">{tracking.delivery_address}</p>
                  </div>
                </div>
              </>
            )}

            <Separator />

            {/* Items */}
            <div>
              <p className="font-medium mb-2">Items ({tracking.items_summary})</p>
              <div className="space-y-2">
                {tracking.items?.map((item, i) => (
                  <div key={i} className="flex justify-between text-sm">
                    <span className="text-muted-foreground">
                      {item.quantity}x {item.name}
                    </span>
                    <span>${item.price.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>

            <Separator />

            {/* Totals */}
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Subtotal</span>
                <span>${tracking.subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Tax</span>
                <span>${tracking.tax.toFixed(2)}</span>
              </div>
              {tracking.delivery_fee > 0 && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Delivery Fee</span>
                  <span>${tracking.delivery_fee.toFixed(2)}</span>
                </div>
              )}
              {tracking.tip > 0 && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Tip</span>
                  <span>${tracking.tip.toFixed(2)}</span>
                </div>
              )}
              <Separator className="my-2" />
              <div className="flex justify-between font-semibold text-base">
                <span>Total</span>
                <span>${tracking.total.toFixed(2)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Receipt Actions */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Receipt className="h-5 w-5" />
              Receipt
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={handleDownloadReceipt}
                disabled={downloadingReceipt}
              >
                {downloadingReceipt ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                Download
              </Button>
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={handleEmailReceipt}
                disabled={emailingReceipt}
              >
                {emailingReceipt ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Mail className="h-4 w-4 mr-2" />
                )}
                Email Receipt
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Special Instructions */}
        {tracking.special_instructions && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Special Instructions</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">{tracking.special_instructions}</p>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
